import { type FC, useState } from "react";
import { useField } from "react-final-form";
import { Autocomplete, Chip, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useTagsQuery,
  useTagTypesAutocompleteQuery,
  useCreateTagMutation,
  type TagsItem,
} from "@/partner/entities/tags";

interface TagGroup {
  typeId: string | null;
  typeTitle: string;
  tags: TagsItem[];
}

const NO_TYPE_KEY = "__none__";

/**
 * Отдельное поле-автокомплит на каждый тип тега организации (в т.ч. пустые —
 * без единого тега) + одно поле «Теги» для тегов без типа. Freesolo: ввод
 * новой строки создаёт тег этого типа через createTag.
 */
export const TagsByTypeField: FC = () => {
  const { t } = useTranslation();
  const { input } = useField<string[]>("tagIds");
  const selectedIds = input.value ?? [];

  const { data } = useTagsQuery({ variables: { rowsPerPage: 500 } });
  const allTags = data?.tags?.nodes ?? [];

  const { data: tagTypesData } = useTagTypesAutocompleteQuery();
  const tagTypes = (tagTypesData?.autocomplete?.options ?? []).filter(
    (option): option is { value: string; label: string } => option !== null
  );

  const [createTag] = useCreateTagMutation({ refetchQueries: ["TagsList"] });
  const [creatingKey, setCreatingKey] = useState<string | null>(null);

  const groupsMap = new Map<string, TagGroup>();
  // Секция на каждый тип тега организации, даже если у него ещё нет тегов
  for (const type of tagTypes) {
    groupsMap.set(type.value, {
      typeId: type.value,
      typeTitle: type.label,
      tags: [],
    });
  }
  groupsMap.set(NO_TYPE_KEY, {
    typeId: null,
    typeTitle: t("videos.tagsField.noTypeGroupLabel"),
    tags: [],
  });
  for (const tag of allTags) {
    const key = tag.type?.id ?? NO_TYPE_KEY;
    const existing = groupsMap.get(key);
    if (existing) {
      existing.tags.push(tag);
    } else {
      groupsMap.set(key, {
        typeId: tag.type?.id ?? null,
        typeTitle: tag.type?.title ?? t("videos.tagsField.noTypeGroupLabel"),
        tags: [tag],
      });
    }
  }
  const groups = Array.from(groupsMap.values());

  const handleChange = async (
    group: TagGroup,
    newValue: (TagsItem | string)[]
  ) => {
    const typed = newValue.find((v): v is string => typeof v === "string");
    // Свободный ввод: лишние пробелы схлопываем, хвостовую запятую срезаем
    // («MILF,» после Enter посреди перечисления через запятую).
    const newTitle = typed
      ? typed
          .replace(/\s+/g, " ")
          .replace(/[\s,;]+$/u, "")
          .trim()
      : "";
    const existingSelected = newValue.filter(
      (v): v is TagsItem => typeof v !== "string"
    );

    const groupSelectedIds = existingSelected.map((t) => t.id);
    const otherIds = selectedIds.filter(
      (id) => !group.tags.some((t) => t.id === id)
    );

    if (newTitle) {
      // Точное совпадение с тегом группы (без учёта регистра) выбирает его,
      // а не создаёт дубль: бэкенд такой дубль всё равно отклонит
      // (TAG_ALREADY_EXISTS), но пользователь ожидает выбор, а не ошибку.
      const existing = group.tags.find(
        (t) => t.title.trim().toLowerCase() === newTitle.toLowerCase()
      );
      if (existing) {
        input.onChange([
          ...otherIds,
          ...Array.from(new Set([...groupSelectedIds, existing.id])),
        ]);
        return;
      }

      const key = group.typeId ?? NO_TYPE_KEY;
      setCreatingKey(key);
      try {
        const { data: created } = await createTag({
          variables: {
            input: {
              title: newTitle,
              ...(group.typeId ? { typeId: group.typeId } : {}),
            },
          },
        });
        const createdId = created?.createTag?.id;
        input.onChange([
          ...otherIds,
          ...groupSelectedIds,
          ...(createdId ? [createdId] : []),
        ]);
      } finally {
        setCreatingKey(null);
      }
      return;
    }

    input.onChange([...otherIds, ...groupSelectedIds]);
  };

  return (
    <Stack spacing={2}>
      {groups.map((group) => {
        const key = group.typeId ?? NO_TYPE_KEY;
        const value = group.tags.filter((t) => selectedIds.includes(t.id));

        return (
          <Autocomplete<TagsItem, true, false, true>
            key={key}
            multiple
            freeSolo
            options={group.tags}
            value={value}
            loading={creatingKey === key}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.title
            }
            isOptionEqualToValue={(option, val) =>
              typeof option !== "string" &&
              typeof val !== "string" &&
              option.id === val.id
            }
            onChange={(_event, updatedValue) =>
              void handleChange(group, updatedValue)
            }
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={typeof option === "string" ? option : option.title}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={group.typeTitle}
                placeholder={t("videos.tagsField.addPlaceholder")}
              />
            )}
          />
        );
      })}
    </Stack>
  );
};
